"use client";

import React, { useEffect, useState } from 'react';
import styles from './ValuationStateCard.module.scss';
import { MyProfileStateCardData } from '@components/molecules/MyProfileStateCard/MyProfileStateCard.data';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import assetLoader from '@utils/internalImageLoder';
import { trackEventWithCommonProps, EventType } from '@utils/eventTracker';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import { PAGE_TYPES } from '@constants/pageTypes';
import Link from 'next/link';
import Text from '../Text';
import { formatReportDate } from '@utils/formatDate';
import { useEqualHeights } from '@hooks/useEqualHeights';

interface ValuationStateCardProps {
  card: MyProfileStateCardData;
  cityName?: string;
  isLoggedIn?: any;
  valuationData: {
    success: boolean,
    data: any
  };
}

const ValuationStateCard: React.FC<ValuationStateCardProps> = ({ card, cityName, isLoggedIn, valuationData }) => {

  const router = useRouter();
  const pathname = usePathname();
  const valuationHref = `${process.env.NEXT_PUBLIC_FRONTEND_HOST}/used/car-valuation/`;
  const { showLoader, hideLoader } = useGlobalLoader();
  const [carValuationData, setCarValuationData] = useState<any>([]);
  const [carValuationShow, setCarValuationShow] = useState<any>(false);
  
  const { elementRef: mainInfoRef } = useEqualHeights({ 
    sectionType: 'mainInfo',
    dependencies: [isLoggedIn, carValuationData, carValuationShow],
  });
  const { elementRef: cardFooterRef } = useEqualHeights({ 
    sectionType: 'cardFooter',
    dependencies: [isLoggedIn, carValuationData, carValuationShow],
  });

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  useEffect(()=> {
    const fetchAllCarValuationData = async() => {
      try {
        if(valuationData?.success){
          setCarValuationData(valuationData?.data);
        }
      } catch (error) {
        console.error("Error while fetching car valuation data");
      } finally {
        setCarValuationShow(true);
      }
    }
    fetchAllCarValuationData();
  },[]);

  const handleClick = async (cardName?: any) => {
    showLoader('Loading...');
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.HOME_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_sell_car",
        city_name: cityName,
        utm_source: "ackodrive",
        utm_medium: "ad2_hompage",
        ...(cardName && {
          card_name: cardName
      })
      },
    });
    router.push("/used/car-valuation/");
  }

  const handleCarValuation = async(item: any) => {
    const eventName = item.report_url ? "Available Car Valuation Report" : "Unavailable Car Valuation Report";

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.HOME_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_sell_car",
        card_name: eventName,
        city_name: cityName,
        registration_number: item?.registration_number,
        utm_source: "ackodrive",
        utm_medium: "ad2_hompage"
      },
    });

    if(item.report_url){
      window.location.href = item?.report_url;
    } else {
      window.location.href = `https://ackodrive.com/used/car-valuation/?regno=${item?.registration_number}&deeplink=true`;
    }
  }

  return (
    <div className={styles.cardContainer}>
      <div className={styles.cardContent}>
        <div className={styles.mainInfo} ref={mainInfoRef} data-section-type="mainInfo">
          <div className={styles.IconWitharrow}>
            <div className={styles.innerTitleWithIcon} >
              <Image
                src={`${assetLoader()}${card.icon}`}
                alt='Arrow Right'
                fill
                className={styles.ctaSVG}
                priority
                onClick={() => handleClick()}
              />
              <Link
                href={valuationHref}
                onClick={() => handleClick()}
                className={styles.ctaButton}
              >
                <Image
                  src={`${assetLoader()}/icons/Arrow_right.svg`}
                  alt={card.ctaIconName}
                  fill
                  className={styles.ctaIcon}
                  priority
                />
              </Link>
            </div>
              <Link href={valuationHref} onClick={() => handleClick()}>
                <h3 className={styles.title}>{card.title}</h3>
              </Link>
          </div>
          <Text className={styles.subText}>
            {card.subText.map((segment: any, segmentIndex: any) => (
              <span
                key={segmentIndex}
                className={
                  segment.color
                    ? `${styles[segment.color]} ${styles.subTextColored}`
                    : ""
                }
              >
                {segment.text}
              </span>
            ))}
          </Text>
        </div>
        
        {
          carValuationShow && (
            <div className={styles.cardFooter} ref={cardFooterRef} data-section-type="cardFooter">
              {!isLoggedIn || carValuationData.length === 0 ? (
                <div 
                  className={styles.carValAddVehicle} 
                  onClick={() => handleClick("Add Vehicle")}
                >
                  <Image
                    src={`${assetLoader()}/icons/addBack.svg`}
                    alt='Car Valuation Add Icon'
                    width={32}
                    height={32}
                    className={styles.valuationAddIcon}
                  />
                  <Text className={styles.addVehicle}>Add your car to check resale value</Text>
                </div>
              ) : (
                <div className={`${styles.InnerContainer} ${carValuationData.length > 1 ? styles.scrollableInnerContainer : ''}`} >
                  {
                    carValuationData.length > 0 && carValuationData.map((item: any, index: any) => (
                      <div 
                        className={`${styles.valuationContainer} ${carValuationData.length > 1 ? styles.moreValuationCar : ''}`} 
                        key={index}
                        onClick={ () => handleCarValuation(item)}
                      >
                        <Image
                          src={item.report_url && item.report_date ? `${assetLoader()}/icons/valuationReportPresent.svg` : `${assetLoader()}/icons/ValuationReportAbsent.svg` }
                          alt='Car Valuation Icon'
                          width={48}
                          height={48}
                          className={styles.valuationIcon}
                        />
                        <div className={styles.valuationDetails}>
                          <Text className={styles.valuationRegNumber}>
                            {item.registration_number}
                          </Text>
                          {
                            item.report_url && item.report_date ? (
                              <Text className={styles.valuationStatus}>
                                Your Car Valuation report {item.report_date ? `for ${formatReportDate(item.report_date)}` : '' } is ready!
                              </Text>
                            ) : (
                              <Text className={styles.valuationStatus}>
                                Check resale value of your car
                              </Text>
                            )
                          }
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          )
        }
      </div>
    </div>
  );
};

export default ValuationStateCard;
